<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2">
      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.subject_id"
          @change="applyFilters"
        >
          <option :value="null" disabled>{{ t('filters.chooseSubject') }}</option>
          <option value="">{{ t('filters.allSubjects') }}</option>
          <option
            v-for="subject in subjects"
            :key="subject.id"
            :value="subject.id"
          >
            {{ subject.name }}
          </option>
        </select>
      </div>

      <div class="col-6 col-md-2 col-lg-2" v-if="isTeacher && teacherClasses.length > 0">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.class_id"
          @change="handleClassChange"
        >
          <option :value="null" disabled>{{ t('filters.chooseClass') }}</option>
          <option value="">{{ t('filters.allClasses') }}</option>
          <option
            v-for="classItem in teacherClasses"
            :key="classItem.id"
            :value="classItem.id"
          >
            {{ classItem.name }}
          </option>
        </select>
      </div>

      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.chapter_subject_id"
          @change="applyFilters"
          :disabled="!localFilters.subject_id"
        >
          <option :value="null" disabled>{{ t('filters.chooseChapter') }}</option>
          <option value="">{{ t('filters.allChapters') }}</option>
          <option
            v-for="chapter in chapterSubjects"
            :key="chapter.id"
            :value="chapter.id"
          >
            {{ chapter.chapter?.name || chapter.name || t('common.notAvailable') }}
          </option>
        </select>
      </div>

      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.question_type_id"
          @change="applyFilters"
        >
          <option :value="null" disabled>{{ t('filters.chooseType') }}</option>
          <option value="">{{ t('filters.allTypes') }}</option>
          <option
            v-for="type in questionTypes"
            :key="type.id"
            :value="type.id"
          >
            {{ type.title }}
          </option>
        </select>
      </div>

      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.question_level_id"
          @change="applyFilters"
        >
          <option :value="null" disabled>{{ t('filters.chooseLevel') }}</option>
          <option value="">{{ t('filters.allLevels') }}</option>
          <option
            v-for="level in questionLevels"
            :key="level.id"
            :value="level.id"
          >
            {{ level.name }}
          </option>
        </select>
      </div>

      <div class="col-md-1 d-none d-md-block">
        <button
          class="btn btn-outline-secondary btn-sm w-100"
          @click="resetFilters"
          :title="t('filters.reset')"
        >
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>

      <div class="col-12 col-md-2 col-lg-2 position-relative">
        <input
          type="text"
          class="form-control form-control-sm ps-5"
          :placeholder="t('filters.searchQuestions')"
          v-model="localFilters.search"
          @input="handleSearchInput"
          style="padding-right: 12px"
        />
        <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
          <i class="fas fa-search"></i>
        </span>
      </div>

      <div class="col-6 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ t('filters.reset') }}
        </button>
      </div>

      <div class="col-6 col-md-1 col-lg-1">
        <button
          class="btn btn-secondary btn-sm w-100"
          @click="$emit('add')"
        >
          <i class="fas fa-plus"></i>
          {{ t('filters.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, onUnmounted, computed } from 'vue';
import api from '@/api';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const store = useStore();

const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      search: '',
      subject_id: null,
      class_id: null,
      chapter_subject_id: null,
      question_type_id: null,
      question_level_id: null
    })
  }
});

const emit = defineEmits(['update:filters', 'apply-filters', 'reset-filters', 'add']);

const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');

const localFilters = ref(JSON.parse(JSON.stringify(props.filters)));
const subjects = ref([]);
const chapterSubjects = ref([]);
const questionTypes = ref([]);
const questionLevels = ref([]);
const teacherClasses = ref([]);

let debounceTimer = null;

const debounceSearch = (fn, delay) => {
  return (...args) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fn(...args);
      debounceTimer = null;
    }, delay);
  };
};

const debouncedApplyFilters = debounceSearch(() => applyFilters(), 500);
const handleSearchInput = () => debouncedApplyFilters();

const fetchTeacherClasses = async () => {
  if (!isTeacher.value) return;

  try {
    const response = await api.get(`/teachers/${store.state.user.id}/classes`);
    teacherClasses.value = response.data.data || [];

    if (teacherClasses.value.length === 1 && !localFilters.value.class_id) {
      localFilters.value.class_id = teacherClasses.value[0].id;
    }
  } catch (error) {
    console.error('Error fetching teacher classes:', error);
  }
};

const applyFilters = () => {
  const filtersToEmit = {
    search: localFilters.value.search || '',
    subject_id: localFilters.value.subject_id || null,
    class_id: localFilters.value.class_id || null,
    chapter_subject_id: localFilters.value.chapter_subject_id || null,
    question_type_id: localFilters.value.question_type_id || null,
    question_level_id: localFilters.value.question_level_id || null,
    ...(isTeacher.value ? { teacher_id: store.state.user.id } : {})
  };

  emit('update:filters', filtersToEmit);
  emit('apply-filters');
};

const handleClassChange = async () => {
  localFilters.value.chapter_subject_id = null;

  if (localFilters.value.subject_id) {
    await fetchChapterSubjects();
  }

  applyFilters();
};

const resetFilters = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;

  localFilters.value = {
    search: '',
    subject_id: null,
    class_id: null,
    chapter_subject_id: null,
    question_type_id: null,
    question_level_id: null
  };

  emit('update:filters', localFilters.value);
  emit('reset-filters');
};

const fetchSubjects = async () => {
  try {
    const response = await api.get('/subjects');
    subjects.value = response.data.data || [];
  } catch (error) {
    console.error('Error fetching subjects:', error);
  }
};

const fetchChapterSubjects = async () => {
  if (!localFilters.value.subject_id) {
    chapterSubjects.value = [];
    return;
  }

  try {
    const response = await api.get('/chapter-subjects', {
      params: {
        subject_id: localFilters.value.subject_id,
        class_id: localFilters.value.class_id || null
      }
    });

    chapterSubjects.value = response.data.data || [];
  } catch (error) {
    console.error('Error fetching chapter subjects:', error);
    chapterSubjects.value = [];
  }
};

const fetchQuestionTypes = async () => {
  try {
    const response = await api.get('/question-types');
    questionTypes.value = response.data.data || [];
  } catch (error) {
    console.error('Error fetching question types:', error);
  }
};

const fetchQuestionLevels = async () => {
  try {
    const response = await api.get('/question-levels');
    questionLevels.value = response.data.data || [];
  } catch (error) {
    console.error('Error fetching question levels:', error);
  }
};

watch(
  () => localFilters.value.subject_id,
  async (newVal) => {
    localFilters.value.chapter_subject_id = null;

    if (newVal) {
      await fetchChapterSubjects();
    } else {
      chapterSubjects.value = [];
    }

    applyFilters();
  }
);

watch(
  () => props.filters,
  (newVal) => {
    localFilters.value = JSON.parse(JSON.stringify(newVal));
  },
  { deep: true }
);

onMounted(() => {
  Promise.all([
    fetchSubjects(),
    fetchQuestionTypes(),
    fetchQuestionLevels()
  ]).then(async () => {
    if (isTeacher.value) {
      await fetchTeacherClasses();
    }

    if (localFilters.value.subject_id) {
      await fetchChapterSubjects();
    }
  });
});

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>